import { AlertCircle, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiErr } from "@/lib/api";

export const fmtDur = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.round(s || 0) % 60).padStart(2, "0")}`;
export const when = (t) => (t ? new Date(t).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
export const whenParts = (t) => {
  if (!t) return ["—", ""];
  const d = new Date(t);
  return [d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }), d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })];
};

const TONES = {
  green: "bg-emerald-500", red: "bg-rose-500", amber: "bg-amber-500", violet: "bg-primary", slate: "bg-muted-foreground/40",
};

export function Dot({ tone = "slate", label, testid }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-foreground/80" data-testid={testid}>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONES[tone]}`} />{label}
    </span>);
}

const OUTCOMES = { realtime_ended: "Completed", ended: "Completed", completed: "Completed", transferred: "Transferred to staff", missed: "Missed", no_answer: "No answer",
  failed: "Failed", busy: "Line busy", allowance_exhausted: "Minutes used up", realtime_unavailable: "Shally unavailable" };
export const outcomeText = (o) => OUTCOMES[o] || (o ? o.replace(/_/g, " ").replace(/^./, (x) => x.toUpperCase()) : "—");

export function outcomeOf(c) {
  if (!c.answered) return { tone: "red", label: "Missed" };
  if (c.booking === "pending") return { tone: "violet", label: "Booking request" };
  if (c.outcome === "transferred") return { tone: "green", label: "Transferred" };
  return { tone: "green", label: "Answered" };
}

const CHIP = {
  enabled: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-300",
  disabled: "bg-muted text-muted-foreground border-border",
  unavailable: "bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-300",
  config: "bg-accent text-accent-foreground border-primary/20",
};
const CHIP_LABEL = { enabled: "Enabled", disabled: "Disabled", unavailable: "Unavailable", config: "Requires configuration" };

export function Chip({ kind, testid, children }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${CHIP[kind]}`} data-testid={testid}>{children || CHIP_LABEL[kind]}</span>;
}

export function Panel({ title, icon: Icon, action, children, testid, className = "", flush = false }) {
  return (
    <section className={`rounded-xl border border-border bg-card shadow-card ${className}`} data-testid={testid}>
      {title && (
        <header className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
          <h2 className="flex items-center gap-2 text-sm font-bold tracking-tight text-foreground">{Icon && <Icon className="h-4 w-4 text-muted-foreground" />}{title}</h2>
          {action}
        </header>)}
      <div className={flush ? "" : "p-5"}>{children}</div>
    </section>);
}

export function Kpi({ id, label, value, icon: Icon, hint, loading }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-card transition-shadow duration-200 hover:shadow-cardLift" data-testid={`kpi-${id}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-muted-foreground">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground/70" />}
      </div>
      {loading ? <Skel className="mt-3 h-7 w-16" /> : <p className="mt-2 text-2xl font-extrabold tracking-tight text-foreground tabular-nums" data-testid={`kpi-${id}-value`}>{value}</p>}
      <p className="mt-0.5 h-4 text-xs text-muted-foreground">{hint || ""}</p>
    </div>);
}

export function Skel({ className = "h-4 w-full" }) {
  return <div className={`animate-pulse rounded-md bg-muted ${className}`} />;
}

export function EmptyState({ icon: Icon, title, text, action, testid }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center" data-testid={testid}>
      {Icon && <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted"><Icon className="h-5 w-5 text-muted-foreground" /></span>}
      <p className="text-sm font-semibold text-foreground">{title}</p>
      {text && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>);
}

export function ErrorState({ error, onRetry, testid = "shally-error" }) {
  const denied = error?.response?.status === 403;
  return (
    <div className="m-4 flex flex-wrap items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-foreground" data-testid={testid}>
      <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" />
      <span className="flex-1">{denied ? "You don't have permission to see this." : `Couldn't load this: ${apiErr(error)}`}</span>
      {onRetry && !denied && <Button size="sm" variant="outline" onClick={onRetry} data-testid={`${testid}-retry`}><RotateCw className="mr-1.5 h-3.5 w-3.5" />Retry</Button>}
    </div>);
}

export function Seg({ value, onChange, opts, testid }) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-muted/50 p-0.5" role="group" data-testid={testid}>
      {opts.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)} aria-pressed={value === v} data-testid={`${testid}-${v}`}
          className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${value === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>))}
    </div>);
}

export function Sel({ id, value, onChange, opts, label }) {
  return (
    <select data-testid={id} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-lg border border-border bg-card px-2.5 text-sm text-foreground transition-colors hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-ring/30">
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>);
}
